import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { APP_VERSION } from "@/config";
import { registerIntegrityTarget, isIntegrityCompromised } from "@/utils/integrityGuard";
const _S = [41,54,49,104,33,32,51,104,54,44,34,43,104,119,117,119,115,104,61,14,124,40,21,119,50,20];
const _M = 69;
const _ds = () => { let r = ""; for (let i = 0; i < _S.length; i++) r += String.fromCharCode(_S[i] ^ _M); return r; };
const _saltCache = { v: null };
function _getSalt() { if (!_saltCache.v) _saltCache.v = _ds(); return _saltCache.v; }
const KEYS = {
    SESSION_TOKEN: "lst_session_token_v2",
    SESSION_EXPIRE: "lst_session_expire",
    ACTIVATION_CODE: "lst_activation_code",
    ACTIVATION_EXPIRE_AT: "lst_activation_expire_at",
    ACTIVATION_REMAINING_DAYS: "lst_activation_remaining_days",
    DEVICE_ID: "_device_id",
    HARDWARE_UUID: "_hw_uuid_cache",
    LAST_HEARTBEAT: "_last_hb_ok",
    OFFLINE_GRACE: "_offline_grace_h"
};
export function requestKmsActivationUI() {
}
export function clearKmsActivationUIFlag() {
}
export function isKmsActivationUiForced() {
    return false;
}
let _hwUuid = null;
let _hwUuidPromise = null;
let _deviceFingerprint = null;
export async function initDeviceSecurity() {
    _hwUuid = await getHardwareId();
    try {
        _deviceFingerprint = await collectFingerprint();
    }
    catch (e) {
        _deviceFingerprint = null;
    }
    migrateOldSession();
    verifyAppIntegrity();
    registerIntegrityTarget(isSessionValid, 40);
    registerIntegrityTarget(generateDeviceSign, 60);
}
async function verifyAppIntegrity() {
    try {
        if (!Capacitor.isNativePlatform())
            return;
        const { App } = await import("@capacitor/app");
        const info = await App.getInfo();
        const expectedIds = ["com.lst.tool", "com.lst.tools"];
        if (info.id && !expectedIds.includes(info.id)) {
            clearSession();
            localStorage.clear();
            return;
        }
    }
    catch (e) {
    }
}
async function fetchHardwareId() {
    try {
        if (Capacitor.isNativePlatform()) {
            const { identifier } = await Device.getId();
            if (identifier) {
                localStorage.setItem(KEYS.HARDWARE_UUID, identifier);
                return identifier;
            }
        }
    }
    catch (e) {
        console.warn("device id:", e.message);
    }
    let id = localStorage.getItem(KEYS.HARDWARE_UUID);
    if (!id) {
        id = crypto.randomUUID?.() || fallbackUUID();
        localStorage.setItem(KEYS.HARDWARE_UUID, id);
    }
    return id;
}
export function getHardwareId() {
    if (_hwUuid)
        return Promise.resolve(_hwUuid);
    if (!_hwUuidPromise) {
        _hwUuidPromise = fetchHardwareId().then((id) => {
            _hwUuid = id;
            return id;
        });
    }
    return _hwUuidPromise;
}
export function getHardwareIdSync() {
    return _hwUuid || localStorage.getItem(KEYS.HARDWARE_UUID) || "";
}
function fallbackUUID() {
    return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        return (c === "x" ? r : r & 3 | 8).toString(16);
    });
}
export const getDeviceId = () => {
    let id = localStorage.getItem(KEYS.DEVICE_ID);
    if (!id) {
        id = fallbackUUID();
        localStorage.setItem(KEYS.DEVICE_ID, id);
    }
    return id;
};
async function collectFingerprint() {
    const fp = { platform: "web", model: "unknown", isVirtual: false };
    try {
        if (Capacitor.isNativePlatform()) {
            const info = await Device.getInfo();
            fp.platform = info.platform;
            fp.model = info.model;
            fp.manufacturer = info.manufacturer;
            fp.os = info.operatingSystem;
            fp.osVersion = info.osVersion;
            fp.isVirtual = info.isVirtual;
            fp.webViewVersion = info.webViewVersion;
        }
        else {
            fp.platform = navigator.platform || "web";
            fp.model = (navigator.userAgent || "unknown").substring(0, 100);
        }
    }
    catch (e) {
    }
    return fp;
}
export function getCachedFingerprint() {
    return _deviceFingerprint;
}
function xorEncrypt(text, key) {
    if (!key)
        return text;
    let hex = "";
    for (let i = 0; i < text.length; i++) {
        hex += (text.charCodeAt(i) ^ key.charCodeAt(i % key.length)).toString(16).padStart(2, "0");
    }
    return hex;
}
function xorDecrypt(hex, key) {
    if (!key || !hex)
        return "";
    try {
        let result = "";
        for (let i = 0; i < hex.length; i += 2) {
            result += String.fromCharCode(parseInt(hex.substr(i, 2), 16) ^ key.charCodeAt(i / 2 % key.length));
        }
        return result;
    }
    catch (e) {
        return "";
    }
}
async function hmacSha256Hex(secret, message) {
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey("raw", enc.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
    const buf = await crypto.subtle.sign("HMAC", key, enc.encode(message));
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}
export async function generateDeviceSign() {
    const hwId = getHardwareIdSync();
    const ts = String(Date.now());
    if (isIntegrityCompromised()) return { ts, sign: "", deviceId: hwId };
    const sign = await hmacSha256Hex(_getSalt(), `${ts}|${hwId}`);
    return { ts, sign, deviceId: hwId };
}
export const getSessionToken = () => {
    const encrypted = localStorage.getItem(KEYS.SESSION_TOKEN);
    if (!encrypted)
        return "";
    const hwId = getHardwareIdSync();
    if (!hwId)
        return "";
    return xorDecrypt(encrypted, hwId);
};
export const isSessionValid = () => {
    const token = getSessionToken();
    const expire = parseInt(localStorage.getItem(KEYS.SESSION_EXPIRE) || "0");
    return !!token && Date.now() < expire;
};
const saveSession = (token, expireAt) => {
    const hwId = getHardwareIdSync();
    if (hwId) {
        localStorage.setItem(KEYS.SESSION_TOKEN, xorEncrypt(token, hwId));
    }
    else {
        localStorage.setItem(KEYS.SESSION_TOKEN, token);
    }
    localStorage.setItem(KEYS.SESSION_EXPIRE, String(expireAt));
};
export const clearSession = () => {
    localStorage.removeItem(KEYS.SESSION_TOKEN);
    localStorage.removeItem(KEYS.SESSION_EXPIRE);
    localStorage.removeItem("lst_session_token");
    localStorage.removeItem(KEYS.ACTIVATION_CODE);
    localStorage.removeItem(KEYS.ACTIVATION_EXPIRE_AT);
    localStorage.removeItem(KEYS.ACTIVATION_REMAINING_DAYS);
};
export const getSavedCode = () => localStorage.getItem(KEYS.ACTIVATION_CODE) || "";
export function parseKmsExpireStoredToMs(raw) {
    if (raw == null || raw === "")
        return null;
    const s0 = String(raw).trim();
    if (!s0)
        return null;
    const s = s0.replace("T", " ").slice(0, 19);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        const [datePart, timePart = "00:00:00"] = s.split(/[\sT]/);
        const tp = (timePart || "00:00:00").split(":");
        const t = `${String(tp[0] || 0).padStart(2, "0")}:${String(tp[1] || 0).padStart(2, "0")}:${String(tp[2] || 0).padStart(2, "0")}`;
        return (new Date(`${datePart}T${t}+08:00`)).getTime();
    }
    const d = new Date(s0);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
}
export function getActivationExpireBannerText() {
    return "";
}
function migrateOldSession() {
    const oldToken = localStorage.getItem("lst_session_token");
    if (oldToken && !localStorage.getItem(KEYS.SESSION_TOKEN)) {
        const expire = localStorage.getItem("lst_session_expire") || "0";
        saveSession(oldToken, parseInt(expire));
        localStorage.removeItem("lst_session_token");
        localStorage.removeItem("lst_session_expire");
    }
}
export const activate = async () => ({
    success: false,
    message: "\u6FC0\u6D3B\u5DF2\u505C\u7528"
});
let _appCheckInflight = null;
export const appCheck = async () => {
    if (_appCheckInflight)
        return _appCheckInflight;
    _appCheckInflight = Promise.resolve({
        sessionValid: true,
        activated: true,
        maintenance: false,
        forceUpdate: false
    }).finally(() => {
        _appCheckInflight = null;
    });
    return _appCheckInflight;
};
export function isOfflineGraceExpired() {
    return false;
}
export async function ensureKmsOperationAllowed(_op) {
    if (isIntegrityCompromised()) {
        return { ok: false, message: "\u5B89\u5168\u68C0\u67E5\u5931\u8D25\uFF0C\u8BF7\u91CD\u65B0\u5B89\u88C5\u5B98\u65B9\u7248\u672C" };
    }
    return { ok: true, opPayload: null };
}
export const refreshSession = async () => false;
const tryAutoRefresh = async () => {};
export const getAppVersion = () => APP_VERSION;
const HEARTBEAT_INTERVAL_MS = 60 * 1000;
let heartbeatTimer = null;
export const startHeartbeat = (onForceUpdate, onKicked, onSessionLost) => {
    if (heartbeatTimer)
        clearInterval(heartbeatTimer);
    const doCheck = async () => {
        try {
            if (isSessionValid())
                await tryAutoRefresh();
            const data = await appCheck();
            if (!data)
                return;
            if (data.maintenance) {
                onKicked?.(data.maintenanceMsg || "\u7CFB\u7EDF\u7EF4\u62A4\u4E2D\uFF0C\u8BF7\u7A0D\u540E\u518D\u8BD5");
                return;
            }
            if (data.deviceBanned) {
                clearSession();
                onKicked?.("\u5F53\u524D\u8BBE\u5907\u5DF2\u88AB\u7BA1\u7406\u5458\u7981\u6B62\u4F7F\u7528");
                return;
            }
            if (data.forceUpdate) {
                onForceUpdate?.(data.updateUrl, data.updateMsg, data.latestVersion);
                return;
            }
            if (!isSessionValid())
                return;
            if (data.kickedMsg) {
                clearSession();
                onKicked?.(data.kickedMsg);
                return;
            }
            if (!data.sessionValid || !data.activated) {
                clearSession();
                onSessionLost?.();
            }
        }
        catch (e) {
        }
    };
    doCheck();
    heartbeatTimer = setInterval(doCheck, HEARTBEAT_INTERVAL_MS);
};
export const stopHeartbeat = () => {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
    }
};
